import React, { useState, useEffect } from "react";
import { createGroup, getGroups, updateGroup, deleteGroup } from "../api";
import toast, { Toaster } from 'react-hot-toast';
import Swal from 'sweetalert2';
import { 
  RectangleGroupIcon,
  PlusCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';

const QueGrp = () => {
  const [groups, setGroups] = useState([]);
  const [title, setTitle] = useState("");
  const [editingGroup, setEditingGroup] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredGroups, setFilteredGroups] = useState([]);

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    const filtered = groups.filter(group => 
      group.Title.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredGroups(filtered);
  }, [searchTerm, groups]);

  const fetchGroups = async () => {
    try {
      const data = await getGroups();
      setGroups(data);
      setFilteredGroups(data);
    } catch (error) {
      toast.error('Failed to fetch groups');
    }
  };

  const validateForm = () => {
    if (title.trim() === "") {
      setErrorMessage("Group title cannot be empty");
      return false;
    }
    if (title.length < 3) {
      setErrorMessage("Group title must be at least 3 characters long");
      return false;
    }
    setErrorMessage("");
    return true;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;
    try {
      await createGroup({ Title: title });
      setTitle("");
      toast.success('Group created successfully!');
      fetchGroups();
    } catch (error) {
      toast.error('Failed to create group');
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    try {
      await updateGroup({ gid: editingGroup, Title: title });
      setEditingGroup(null);
      setTitle("");
      toast.success('Group updated successfully!');
      fetchGroups();
    } catch (error) {
      toast.error('Failed to update group');
    }
  };

  const handleDelete = async (gid) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      customClass: {
        popup: 'rounded-2xl',
        confirmButton: 'rounded-xl px-6 py-2.5',
        cancelButton: 'rounded-xl px-6 py-2.5'
      }
    });

    if (result.isConfirmed) {
      try {
        await deleteGroup(gid);
        toast.success('Group deleted successfully!');
        fetchGroups();
      } catch (error) {
        toast.error('Failed to delete group');
      }
    }
  };

  const handleFormReset = () => {
    setEditingGroup(null);
    setTitle("");
    setErrorMessage("");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <Toaster position="top-right" />

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
                <RectangleGroupIcon className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Question Groups</h1>
                <p className="text-sm text-gray-500">
                  Manage and organize question categories
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-purple-600">{groups.length}</p>
              <p className="text-xs text-gray-500">Total Groups</p>
            </div>
          </div>
        </div>

        {/* Create/Edit Form */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center space-x-2 mb-4">
            {editingGroup ? (
              <PencilSquareIcon className="h-5 w-5 text-blue-600" />
            ) : (
              <PlusCircleIcon className="h-5 w-5 text-green-600" />
            )}
            <h2 className="text-lg font-semibold text-gray-800">
              {editingGroup ? 'Edit Group' : 'Create New Group'}
            </h2>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={`w-full px-4 py-3 bg-gray-50 border rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all ${
                  errorMessage ? 'border-red-500' : 'border-gray-200'
                }`}
                placeholder="Enter group title (min 3 characters)"
              />
              {errorMessage && (
                <div className="flex items-center mt-2 text-red-600 text-sm">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errorMessage}
                </div>
              )}
            </div>

            <div className="flex gap-2">
              {editingGroup ? (
                <>
                  <button
                    onClick={handleUpdate}
                    className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md"
                  >
                    <CheckCircleIcon className="h-5 w-5" />
                    <span>Update</span>
                  </button>
                  <button
                    onClick={handleFormReset}
                    className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all border border-gray-300"
                  >
                    <XMarkIcon className="h-5 w-5" />
                    <span>Cancel</span>
                  </button>
                </>
              ) : (
                <button
                  onClick={handleCreate}
                  className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transition-all shadow-md"
                >
                  <PlusCircleIcon className="h-5 w-5" />
                  <span>Create Group</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Groups List */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Search Bar */}
          <div className="p-6 border-b border-gray-200">
            <input
              type="text"
              placeholder="Search groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
            />
          </div>

          {/* Groups Grid */}
          {filteredGroups.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
              {filteredGroups.map((group, index) => (
                <div
                  key={group.gid}
                  className={`bg-gradient-to-br ${
                    editingGroup === group.gid 
                      ? 'from-blue-50 to-indigo-50 border-blue-300' 
                      : 'from-gray-50 to-white border-gray-200'
                  } border-2 rounded-xl p-5 hover:shadow-md transition-all`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-md">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-800 text-lg">
                          {group.Title}
                        </h3>
                        <p className="text-xs text-gray-500">
                          Group ID: {group.gid}
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingGroup(group.gid);
                          setTitle(group.Title);
                          setErrorMessage("");
                        }}
                        className="p-2.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-all border border-blue-200"
                        title="Edit Group"
                      >
                        <PencilSquareIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(group.gid)}
                        className="p-2.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all border border-red-200"
                        title="Delete Group"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <div className="flex flex-col items-center justify-center space-y-3">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <RectangleGroupIcon className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-500 font-medium">
                  {searchTerm ? 'No groups found matching your search' : 'No groups available'}
                </p>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                  >
                    Clear search
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Footer Stats */}
          {filteredGroups.length > 0 && (
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>
                  Showing <span className="font-semibold text-gray-800">{filteredGroups.length}</span> of{' '}
                  <span className="font-semibold text-gray-800">{groups.length}</span> groups
                </span>
                <span className="text-xs text-gray-500">
                  Last updated: {new Date().toLocaleDateString()}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QueGrp;
